import { map } from 'ramda';
import React, { FC } from 'react';
import styled, { css } from 'styled-components';

import FlexColToRow from '@ui/molecules/FlexColToRow';

import { ColorTypes, RadiusTypes, SpacingTypes } from '@folx-types/theme';

import theme from '@theme/FolxTheme';

export interface ISkeletonProps {
	animationColor?: ColorTypes;
	borderRadius?: RadiusTypes;
	breakpoint?: string;
	className?: string;
	division?: 'equal' | 'first' | 'last';
	fullHeight?: boolean;
	fullWidth?: boolean;
	gap?: SpacingTypes;
	height?: SpacingTypes;
	repeated?: number;
	structure?: 'single' | 'column' | 'row' | 'colToRow';
	width?: SpacingTypes;
}

const shimmer = css<{ animationColor?: ColorTypes }>`
	&:after {
		position: absolute;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
		transform: translateX(-100%);
		background-image: ${({ animationColor, theme: components }) => `linear-gradient(
			90deg,
			${animationColor ? theme.colors[animationColor] : components.pageContainer.background}00 0%,
			${animationColor ? theme.colors[animationColor] : components.pageContainer.background}30 20%,
			${animationColor ? theme.colors[animationColor] : components.pageContainer.background}60 80%,
			${animationColor ? theme.colors[animationColor] : components.pageContainer.background}00
		)`};
		animation: shimmer 2.5s infinite;
		content: '';
	}

	@keyframes shimmer {
		100% {
			transform: translateX(100%);
		}
	}
`;

interface IStyledSkeletonProps {
	borderRadius: RadiusTypes;
	fullHeight: boolean;
	fullWidth: boolean;
	height: SpacingTypes;
	structure: 'single' | 'column' | 'row' | 'colToRow';
	width: SpacingTypes;
	animationColor?: ColorTypes;
}

const StyledSkeleton = styled.div<IStyledSkeletonProps>`
	position: relative;
	overflow: hidden;
	background: ${theme.colors.darkGray};
	border-radius: ${({ borderRadius }) => theme.radius[borderRadius]};
	height: ${({ height, fullHeight }) => (fullHeight ? '100%' : theme.spacing[height])};
	width: ${({ width, fullWidth }) => (fullWidth ? '100%' : theme.spacing[width])};

	${({ structure }) => structure === 'single' && shimmer}
`;

interface IStyledColToRow {
	gap: SpacingTypes;
	division: 'equal' | 'first' | 'last';
	animationColor?: ColorTypes;
}

interface IStyledStructure extends IStyledColToRow {
	direction: 'column' | 'row';
}

const StyledColToRow = styled(FlexColToRow)<IStyledColToRow>`
	position: relative;
	overflow: hidden;
	align-items: stretch;
	gap: ${({ gap }) => theme.spacing[gap]};
	justify-content: stretch;
	height: 100%;
	width: 100%;

	${StyledSkeleton}:first-child {
		flex-grow: ${({ division }) => division === 'first' && 1};
	}

	${StyledSkeleton}:last-child {
		flex-grow: ${({ division }) => division === 'last' && 1};
	}

	${StyledSkeleton} {
		${shimmer}
	}
`;

const StyledStructure = styled.div<IStyledStructure>`
	position: relative;
	overflow: hidden;
	align-items: stretch;
	display: flex;
	flex-direction: ${({ direction }) => direction};
	gap: ${({ gap }) => theme.spacing[gap]};
	justify-content: stretch;
	height: auto;
	width: 100%;

	${StyledSkeleton}:first-child {
		flex-grow: ${({ division }) => division === 'first' && 1};
	}

	${StyledSkeleton}:last-child {
		flex-grow: ${({ division }) => division === 'last' && 1};
	}

	${StyledSkeleton} {
		${shimmer}
	}
`;

const Skeleton: FC<ISkeletonProps> = ({
	animationColor,
	borderRadius = 'md',
	breakpoint = 'tablet',
	className,
	division = 'equal',
	fullHeight = false,
	fullWidth = false,
	gap = 'xs',
	height = 'md',
	repeated = 3,
	structure = 'single',
	width = height,
}) => {
	const skeleton = (
		<StyledSkeleton
			className={className}
			animationColor={animationColor}
			borderRadius={borderRadius}
			fullHeight={fullHeight}
			fullWidth={fullWidth}
			height={height}
			structure={structure}
			width={width}
		/>
	);

	const Component = structure === 'colToRow' ? StyledColToRow : StyledStructure;

	return structure === 'single' ? (
		skeleton
	) : (
		<Component
			animationColor={animationColor}
			breakpoint={breakpoint}
			gap={gap}
			direction={structure}
			division={division}>
			{map(() => skeleton, Array(repeated))}
		</Component>
	);
};

export default styled(Skeleton)``;
